/*
 * Browser push notification helpers (placement tracker).
 *
 * apiClient: an axios instance whose baseURL is the placement API (e.g. PlacementTrackerAPI's apiClient).
 * audience:  'user' for students, 'coordinator' for coordinators / admins. It picks the API routes:
 *            /user/notifications/...  or  /coordinator/notifications/...
 */

import { toast } from 'react-toastify'

const WORKER_URL = '/workers/push-sw.js'
const WORKER_SCOPE = '/workers/push/'

// Scopes (pathnames) of service workers that must no longer exist:
//   '/'      the old activity-report worker (it captured push subscriptions but has no push handler)
//   '/push/' the earlier push worker scope
const OBSOLETE_SCOPES = ['/', '/push/']

export const isPushSupported = () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window

export const getPermission = () => ( isPushSupported() ? Notification.permission : 'unsupported' )

// VAPID public key (base64url) -> Uint8Array, as PushManager.subscribe expects
const urlBase64ToUint8Array = base64String => {
    const padding = '='.repeat( ( 4 - ( base64String.length % 4 ) ) % 4 )
    const base64 = ( base64String + padding ).replace( /-/g, '+' ).replace( /_/g, '/' )
    const raw = window.atob( base64 )
    return Uint8Array.from( [...raw].map( char => char.charCodeAt( 0 ) ) )
}

const basePath = audience => ( audience === 'coordinator' ? '/coordinator/notifications' : '/user/notifications' )

const scopePath = registration => new URL( registration.scope ).pathname

const findPushRegistration = async () => {
    const registrations = await navigator.serviceWorker.getRegistrations()
    return registrations.find( r => scopePath( r ) === WORKER_SCOPE ) || null
}

const removeObsoleteWorkers = async () => {
    const registrations = await navigator.serviceWorker.getRegistrations()
    const obsolete = registrations.filter( r => OBSOLETE_SCOPES.includes( scopePath( r ) ) )
    await Promise.all( obsolete.map( r => r.unregister().catch( () => false ) ) )
}


export const registerPushWorker = async () => {
    await removeObsoleteWorkers()

    const registration = await navigator.serviceWorker.register( WORKER_URL, {
        scope: WORKER_SCOPE,
        updateViaCache: 'none'       // bypass the browser HTTP cache when checking for a new worker script
    } )
    registration.update().catch( () => { } )
    return registration
}

const saveSubscription = ( apiClient, audience, subscription ) =>
    apiClient.post( `${basePath( audience )}/subscriptions`, { subscription: subscription.toJSON() } )

/**
 * Subscribes this browser and saves the subscription for the logged-in user.
 * Must be called from a user action (a click): browsers only show the permission prompt then.
 * Returns 'granted', 'denied' or 'default' (prompt dismissed).
 */
export const enablePushNotifications = async ( apiClient, audience ) => {
    try {
        if ( !isPushSupported() ) return 'unsupported'

        const permission = await Notification.requestPermission()
        if ( permission !== 'granted' ) return permission

        const registration = await registerPushWorker()
        const { data } = await apiClient.get( `${basePath( audience )}/public-key` )

        const subscription = (await registration.pushManager.getSubscription()) ||
            (await registration.pushManager.subscribe( {
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array( data.data.publicKey )
            } ))

        await saveSubscription( apiClient, audience, subscription )
        return 'granted'
    } catch ( err ) {
        console.error( err )
        toast( 'Failed to enable push notifications. Please try again.' )
    }
}

/**
 * Re-saves the existing subscription for the logged-in user, without prompting. Call it on every app load:
 * it attaches the browser to whoever is logged in now and refreshes a coordinator's branches.
 * It also removes obsolete workers and installs new worker versions (via registerPushWorker), so existing users
 * are migrated automatically the next time they open the app.
 * Returns true when this browser is subscribed.
 */
export const syncPushSubscription = async ( apiClient, audience ) => {
    if ( !isPushSupported() || Notification.permission !== 'granted' ) return false

    const registration = await registerPushWorker()
    let subscription = await registration.pushManager.getSubscription()

    if ( !subscription ) {
        // Permission is still granted but the subscription expired: renew it silently
        const { data } = await apiClient.get( `${basePath( audience )}/public-key` )
        subscription = await registration.pushManager.subscribe( {
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array( data.data.publicKey )
        } )
    }

    await saveSubscription( apiClient, audience, subscription )
    return true
}

/**
 * Turns notifications off for this browser: removes it from the server and unsubscribes locally.
 * Also call it on LOGOUT (before clearing the auth token), so a shared computer stops receiving the user's pushes.
 */
export const disablePushNotifications = async ( apiClient, audience ) => {
    if ( !isPushSupported() ) return

    const registration = await findPushRegistration()
    const subscription = await registration?.pushManager.getSubscription()
    if ( !subscription ) return

    try {
        await apiClient.delete( `${basePath( audience )}/subscriptions`, { data: { endpoint: subscription.endpoint } } )
    } finally {
        await subscription.unsubscribe()
    }
}

/**
 * Listens for notification clicks forwarded by the worker ("open this page in the app") and calls
 * navigate( path ) for same-origin URLs. Returns a function that stops listening.
 */
export const listenForPushNavigation = navigate => {
    if ( !isPushSupported() ) return () => { }

    const onMessage = event => {
        if ( event.data?.type !== 'placement-push-navigate' ) return

        const url = new URL( event.data.url, window.location.origin )
        if ( url.origin === window.location.origin ) {
            navigate( `${url.pathname}${url.search}${url.hash}` )
        }
    }

    navigator.serviceWorker.addEventListener( 'message', onMessage )
    // Messages from a worker that doesn't control this page are only delivered after startMessages()
    navigator.serviceWorker.startMessages()

    return () => navigator.serviceWorker.removeEventListener( 'message', onMessage )
}